import { CopilotChannelContext } from 'copilot-toolkit/channel/CopilotChannelContext';
import { useSubscribeToCopilotChannel } from 'copilot-toolkit/channel/hooks/useSubscribeToCopilotChannel';
import { TOPIC_NAMES } from 'copilot-toolkit/types/Channel';
import { BREEZE_COPILOT } from 'chatspot-core/types/CopilotAssistantTypes';
import Raven from 'raven-js';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSendChatMessage } from '../chat/hooks/useSendChatMessage';
import { useSendMessageAction } from '../chat/hooks/useSendMessageAction';
import { updateParentAppState } from '../chat/slices/parentApp';
import { resetActiveActionPrompt } from '../chat/slices/promptUser';
import { useAssistantSwitcher } from '../hooks/useAssistantSwitcher';
import { useCollaborationParentState, useUpdateCollaborationParentState } from '../hooks/useCollaborationParentState';
import { useUpdateActiveActionToPrompt } from '../hooks/useUpdateActiveActionToPrompt';
import { useHostAppContext } from '../hostAppContext/hooks/useAppContext';
import { promptTemplateSelected } from '../promptTemplates/slices/promptTemplates';
import { resumeThread } from '../threads/actions/resumeThread';
import { EDITOR_PLUGIN } from '../types/PluginTypes';
import { useIsUngatedForCopilotV2 } from '../gating/hooks/useIsUngatedForCopilotV2';
import { copilotV2GetLatestOrCreateThread } from '../threads/actions/copilotV2GetLatestOrCreateThread';
import { selectAblyChannelConnectingState, selectFetchStatus, selectIsReadOnly } from '../chat/selectors/chatv2Selectors';
import { selectAssistantType } from '../assistants/selectors/assistants';

// Define a union type for all possible topic payloads

// Create a union type for all possible pending actions

const CopilotChannelListener = () => {
  const sendMessageAction = useSendMessageAction();
  const sendMessage = useSendChatMessage();
  const isReadOnly = useSelector(selectIsReadOnly);
  const {
    updateParentState,
    updateExtraIntentProperties
  } = useUpdateCollaborationParentState();
  const {
    getParentAppStateByPlugin,
    getParentAppSessionIdByPlugin
  } = useCollaborationParentState();
  const updateActiveActionToPrompt = useUpdateActiveActionToPrompt();
  const dispatch = useDispatch();
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const {
    setIsCopilotExpanded
  } = useHostAppContext();
  const assistantType = useSelector(selectAssistantType);
  const pendingActionRef = useRef(null);
  const fetchStatus = useSelector(selectFetchStatus);
  const ablyChannelConnectingState = useSelector(selectAblyChannelConnectingState);
  const editorPluginState = getParentAppStateByPlugin(EDITOR_PLUGIN);
  const {
    updateAssistantType
  } = useAssistantSwitcher();
  const {
    channel
  } = useContext(CopilotChannelContext);
  const processMessageAction = useCallback(payload => {
    var _payload$messageMetaI, _payload$messageMetaI2, _payload$messageMetaI4, _payload$messageActio;
    if (payload !== null && payload !== void 0 && (_payload$messageMetaI = payload.messageMetaInfo) !== null && _payload$messageMetaI !== void 0 && _payload$messageMetaI.openFullScreen) {
      setIsCopilotExpanded(true, {
        source: 'action'
      });
    }
    if (payload !== null && payload !== void 0 && (_payload$messageMetaI2 = payload.messageMetaInfo) !== null && _payload$messageMetaI2 !== void 0 && _payload$messageMetaI2.copilotAssistantMode) {
      var _payload$messageMetaI3;
      updateAssistantType(payload === null || payload === void 0 || (_payload$messageMetaI3 = payload.messageMetaInfo) === null || _payload$messageMetaI3 === void 0 ? void 0 : _payload$messageMetaI3.copilotAssistantMode);
    }

    // dev note: Special case for editor plugin to store the text of the selected content
    if ((payload === null || payload === void 0 || (_payload$messageMetaI4 = payload.messageMetaInfo) === null || _payload$messageMetaI4 === void 0 ? void 0 : _payload$messageMetaI4.source) === 'PLUGIN' && payload !== null && payload !== void 0 && (_payload$messageActio = payload.messageAction) !== null && _payload$messageActio !== void 0 && _payload$messageActio.data && 'text' in payload.messageAction.data && editorPluginState !== null && editorPluginState !== void 0 && editorPluginState.hasSelection) {
      const text = payload.messageAction.data.text;
      dispatch(updateParentAppState({
        plugin: EDITOR_PLUGIN,
        pluginProps: {
          talkingAboutText: text
        },
        sessionId: getParentAppSessionIdByPlugin(EDITOR_PLUGIN)
      }));
    }
    sendMessageAction(payload.messageAction, payload.messageMetaInfo);
  }, [dispatch, editorPluginState === null || editorPluginState === void 0 ? void 0 : editorPluginState.hasSelection, getParentAppSessionIdByPlugin, sendMessageAction, setIsCopilotExpanded, updateAssistantType]);
  const onMessageActionMessageReceived = useCallback(payload => {
    if (isReadOnly) {
      return;
    }

    // Added temporarily until every user is ungated for CopilotV2
    if (!isUngatedForCopilotV2) {
      processMessageAction(payload);
      return;
    }

    // First, update assistant type to BREEZE_COPILOT
    updateAssistantType(BREEZE_COPILOT);

    // Store the payload with topic to be processed after the assistant type update settles
    pendingActionRef.current = {
      topic: TOPIC_NAMES.SEND_ACTION,
      payload
    };
  }, [isReadOnly, isUngatedForCopilotV2, processMessageAction, updateAssistantType]);
  const onSendMessageMessageReceived = useCallback(({
    messageText,
    messageMetaInfo
  }) => {
    if (isReadOnly) {
      return;
    }
    void sendMessage(messageText, messageMetaInfo);
  }, [isReadOnly, sendMessage]);
  const onLaunchFromCopilotPreview = useCallback(payload => {
    // There may be no payload if there is an error
    // 1. User doesn't have access
    // 3. Something else goes wrong
    // In these cases, we don't want to do anything
    if (payload === null) {
      return;
    }
    if (isUngatedForCopilotV2) {
      dispatch(copilotV2GetLatestOrCreateThread({
        threadId: payload.threadId,
        initialMessage: payload.initialMessage,
        messageLimit: 10,
        messageOffset: undefined
      })).catch(e => Raven.captureException(e, {
        extra: {
          component: 'CopilotChannelListener',
          action: 'onLaunchFromCopilotPreview',
          copilotVersion: '2'
        }
      }));
    } else {
      dispatch(resumeThread({
        initialMessage: payload.initialMessage,
        id: payload.threadId,
        offset: undefined,
        limit: 10
      })).catch(e => Raven.captureException(e, {
        extra: {
          component: 'CopilotChannelListener',
          action: 'onLaunchFromCopilotPreview'
        }
      }));
    }
  }, [dispatch, isUngatedForCopilotV2]);
  const onLaunchWithCustomCopilot = useCallback(payload => {
    updateAssistantType(payload.customAssistantType || BREEZE_COPILOT, payload.customAssistant);
  }, [updateAssistantType]);
  const processOpenPromptTemplate = useCallback(payload => {
    dispatch(promptTemplateSelected({
      templateId: payload.promptTemplateId,
      prefillValues: payload.prefillValues
    }));
  }, [dispatch]);
  const onOpenPromptTemplate = useCallback(payload => {
    if (payload === null) {
      return;
    }

    // Added temporarily until every user is ungated for CopilotV2
    if (!isUngatedForCopilotV2) {
      dispatch(promptTemplateSelected({
        templateId: payload.promptTemplateId,
        prefillValues: payload.prefillValues
      }));
      return;
    }
    updateAssistantType(BREEZE_COPILOT);
    // Store the payload with topic to be processed after the assistant type update settles
    pendingActionRef.current = {
      topic: TOPIC_NAMES.OPEN_PROMPT_TEMPLATE,
      payload
    };
  }, [dispatch, isUngatedForCopilotV2, updateAssistantType]);
  const handleUpdateActiveActionToPrompt = useCallback(payload => {
    if (payload === null) {
      dispatch(resetActiveActionPrompt());
      return;
    }
    updateActiveActionToPrompt(payload);
  }, [dispatch, updateActiveActionToPrompt]);

  // Effect to process pending action after assistant type has been updated to BREEZE_COPILOT
  useEffect(() => {
    if (assistantType === BREEZE_COPILOT && pendingActionRef.current && ablyChannelConnectingState === 'success' && fetchStatus === 'success') {
      const pendingAction = pendingActionRef.current;
      pendingActionRef.current = null;

      // Process the action based on topic type
      switch (pendingAction.topic) {
        case TOPIC_NAMES.SEND_ACTION:
          processMessageAction(pendingAction.payload);
          break;
        case TOPIC_NAMES.OPEN_PROMPT_TEMPLATE:
          processOpenPromptTemplate(pendingAction.payload);
          break;
        default:
          break;
      }
    }
  }, [ablyChannelConnectingState, assistantType, fetchStatus, processMessageAction, processOpenPromptTemplate]);
  useSubscribeToCopilotChannel(TOPIC_NAMES.SEND_ACTION, onMessageActionMessageReceived);
  useSubscribeToCopilotChannel(TOPIC_NAMES.SEND_MESSAGE, onSendMessageMessageReceived);
  useSubscribeToCopilotChannel(TOPIC_NAMES.LAUNCH_FROM_COPILOT_PREVIEW, onLaunchFromCopilotPreview);
  useSubscribeToCopilotChannel(TOPIC_NAMES.LAUNCH_WITH_CUSTOM_COPILOT, onLaunchWithCustomCopilot);
  useSubscribeToCopilotChannel(TOPIC_NAMES.OPEN_PROMPT_TEMPLATE, onOpenPromptTemplate);
  useSubscribeToCopilotChannel(TOPIC_NAMES.COLLABORATE_PARENT_APP_MESSAGE, updateParentState);
  useSubscribeToCopilotChannel(TOPIC_NAMES.COLLABORATE_COPILOT_SET_CONTEXT, updateExtraIntentProperties);
  useSubscribeToCopilotChannel(TOPIC_NAMES.PROMPT_USER_FOR_MORE_INFO, handleUpdateActiveActionToPrompt);
  useEffect(() => {
    if (channel) {
      channel.setState({
        isWidgetActive: true
      });
    }
  }, [channel]);
  return null;
};
export default CopilotChannelListener;